import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { X } from "lucide-react";
import { twMerge } from "tailwind-merge";

// Types for dialog variants
type DialogSize = "sm" | "md" | "lg" | "xl" | "full";
type DialogVariant = "default" | "destructive" | "success" | "warning";

// Props interface for the main Dialog component
interface DialogProps {
  /** Controls the open state of the dialog */
  open?: boolean;
  /** Default open state for uncontrolled usage */
  defaultOpen?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** The trigger element that opens the dialog */
  trigger?: React.ReactNode;
  /** Dialog title */
  title?: React.ReactNode;
  /** Dialog description */
  description?: React.ReactNode;
  /** Dialog content */
  children?: React.ReactNode;
  /** Dialog size */
  size?: DialogSize;
  /** Dialog variant for styling */
  variant?: DialogVariant;
  /** Whether to show the close button */
  showCloseButton?: boolean;
  /** Custom close button */
  closeButton?: React.ReactNode;
  /** Whether clicking outside closes the dialog */
  closeOnOutsideClick?: boolean;
  /** Whether pressing Escape closes the dialog */
  closeOnEscape?: boolean;
  /** Portal container element */
  container?: HTMLElement;
  /** Additional className for overlay */
  overlayClassName?: string;
  /** Additional className for content */
  contentClassName?: string;
  /** Additional className for title */
  titleClassName?: string;
  /** Additional className for description */
  descriptionClassName?: string;
  /** Prevent scrolling when dialog is open */
  preventScroll?: boolean;
  /** Action buttons to render in the footer */
  actions?: React.ReactNode;
  /** Custom footer content */
  footer?: React.ReactNode;
  hideOverlay?: boolean;
}

// Size mapping for width classes
const sizeClasses: Record<DialogSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  full: "max-w-[90vw] h-[90vh]",
};

// Variant mapping for colors
const variantClasses: Record<
  DialogVariant,
  { header: string; closeButton: string }
> = {
  default: {
    header: "border-gray-200",
    closeButton: "hover:bg-gray-100 text-gray-500",
  },
  destructive: {
    header: "border-red-200",
    closeButton: "hover:bg-red-100 text-red-500",
  },
  success: {
    header: "border-green-200",
    closeButton: "hover:bg-green-100 text-green-500",
  },
  warning: {
    header: "border-yellow-200",
    closeButton: "hover:bg-yellow-100 text-yellow-600",
  },
};

// Sub-components for flexible composition
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogOverlay = DialogPrimitive.Overlay;
export const DialogContent = DialogPrimitive.Content;
export const DialogTitle = DialogPrimitive.Title;
export const DialogDescription = DialogPrimitive.Description;
export const DialogClose = DialogPrimitive.Close;

// Main Dialog component
export const Dialog: React.FC<DialogProps> = ({
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  title,
  description,
  children,
  size = "md",
  variant = "default",
  showCloseButton = true,
  closeButton,
  closeOnOutsideClick = true,
  closeOnEscape = true,
  container,
  overlayClassName = "",
  contentClassName = "",
  titleClassName = "",
  descriptionClassName = "",
  preventScroll = true,
  actions,
  footer,
  hideOverlay,
}) => {
  const variantStyle = variantClasses[variant];

  return (
    <DialogPrimitive.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
    >
      {trigger && (
        <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
      )}

      <DialogPrimitive.Portal container={container}>
        {!hideOverlay && (
          <DialogPrimitive.Overlay
            className={`data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-40 bg-black/50 backdrop-blur-sm ${overlayClassName} `}
          />
        )}

        <DialogPrimitive.Content
          onInteractOutside={(e) => {
            if (!closeOnOutsideClick) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            if (!closeOnEscape) {
              e.preventDefault();
            }
          }}
          className={twMerge(
            `data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed top-[50%] left-[50%] z-40 w-full translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white shadow-xl duration-200 dark:bg-gray-800 ${sizeClasses[size]} ${size === "full" ? "flex flex-col" : ""}`,
            contentClassName
          )}
        >
          {!title && (
            <VisuallyHidden asChild>
              <DialogPrimitive.Title>Dialog</DialogPrimitive.Title>
            </VisuallyHidden>
          )}
          {(title || description || showCloseButton) && (
            <div
              className={`flex items-center justify-between border-b p-6 ${variantStyle.header} ${size === "full" ? "shrink-0" : ""} `}
            >
              <div className="flex-1">
                {title && (
                  <DialogPrimitive.Title
                    className={`text-lg leading-none font-semibold tracking-tight ${variant === "destructive" ? "text-red-600 dark:text-red-400" : ""} ${variant === "success" ? "text-green-600 dark:text-green-400" : ""} ${variant === "warning" ? "text-yellow-600 dark:text-yellow-400" : ""} ${titleClassName} `}
                  >
                    {title}
                  </DialogPrimitive.Title>
                )}
                {description && (
                  <DialogPrimitive.Description
                    className={`mt-2 text-sm text-gray-500 dark:text-gray-400 ${descriptionClassName} `}
                  >
                    {description}
                  </DialogPrimitive.Description>
                )}
              </div>

              {showCloseButton && (
                <DialogPrimitive.Close asChild>
                  {closeButton || (
                    <button
                      className={`rounded-full p-2 transition-colors ${variantStyle.closeButton} focus:ring-2 focus:ring-gray-400 focus:outline-none`}
                      aria-label="Close dialog"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </DialogPrimitive.Close>
              )}
            </div>
          )}

          <div
            className={`p-6 ${size === "full" ? "flex-1 overflow-auto" : ""} ${!title && !description && !showCloseButton ? "pt-6" : ""} `}
          >
            {children}
          </div>

          {(actions || footer) && (
            <div
              className={`flex items-center justify-end gap-3 border-t border-gray-200 p-6 dark:border-gray-700 ${size === "full" ? "flex-shrink-0" : ""} `}
            >
              {actions && <div className="flex gap-3">{actions}</div>}
              {footer}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

// Pre-configured dialog variants for common use cases
interface ConfirmDialogProps extends Omit<DialogProps, "variant" | "actions"> {
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: DialogVariant;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  onConfirm,
  onCancel,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmVariant = "destructive",
  ...props
}) => {
  return (
    <Dialog
      {...props}
      variant={confirmVariant}
      actions={
        <>
          <DialogClose asChild>
            <button
              onClick={onCancel}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:outline-none"
            >
              {cancelText}
            </button>
          </DialogClose>
          <DialogClose asChild>
            <button
              onClick={onConfirm}
              className={`rounded-md px-4 py-2 text-sm font-medium text-white focus:ring-2 focus:ring-offset-2 focus:outline-none ${confirmVariant === "destructive" ? "bg-red-600 hover:bg-red-700 focus:ring-red-500" : ""} ${confirmVariant === "success" ? "bg-green-600 hover:bg-green-700 focus:ring-green-500" : ""} ${confirmVariant === "warning" ? "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500" : ""} ${confirmVariant === "default" ? "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500" : ""} `}
            >
              {confirmText}
            </button>
          </DialogClose>
        </>
      }
    />
  );
};

// Alert dialog variant
export const AlertDialog: React.FC<DialogProps> = (props) => {
  return (
    <Dialog {...props} showCloseButton={false} closeOnOutsideClick={false} />
  );
};

export default Dialog;
